import React from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import type { DashboardStats } from '../../data/requestDashboard';
import { TODAY_LABEL } from '../../data/requestDashboard';
import { fmtDec, fmtNum, pctChange } from '../../utils/requestDashboard';
import { DateRangePicker, type DateRange } from './DateRangePicker';
export type KpiTarget = 'requests' | 'open' | 'late' | 'sla' | 'survey';
interface DashboardKpisProps {
  stats: DashboardStats;
  /** One period for the whole page: changing it in any card updates them all. */
  dateRange: DateRange;
  onDateRangeChange: (r: DateRange) => void;
  onSelect: (target: KpiTarget) => void;
}
type Tone = 'good' | 'bad' | 'neutral';
const TONE_TEXT: Record<Tone, string> = {
  good: 'text-emerald-600 font-medium',
  bad: 'text-red-600 font-medium',
  neutral: 'text-slate-500'
};
function KpiCard({
  tag,
  label,
  value,
  suffix,
  note,
  tone,
  detail,
  cta,
  onClick,
  dateRange,
  onDateRangeChange
}: {
  tag?: string;
  label: string;
  value: string;
  suffix?: string;
  note: string;
  tone: Tone;
  detail?: string;
  cta: string;
  onClick: () => void;
  dateRange: DateRange;
  onDateRangeChange: (r: DateRange) => void;
}) {
  return <div className="relative border border-slate-200 rounded-xl shadow-sm bg-white hover:border-[#4F46E5] hover:shadow-md transition-all" data-id="element-5361" data-mp="2g1h4ge36r08v">
      <button type="button" onClick={onClick} aria-label={`${label} : ${value}${suffix ?? ''}. ${cta}`} className="w-full h-full text-left p-5 flex flex-col gap-2 rounded-xl focus:outline-none focus-visible:ring-2 focus-visible:ring-[#4F46E5]/40" data-id="element-5362" data-mp="2kts4dgxxem1f">
        <div className="pr-20 min-w-0" data-id="element-5363" data-mp="2ysru6rx6d6z6">
          <span className="text-sm font-medium text-slate-500 font-body" data-id="element-5364" data-mp="33rbfcq3y2eqh">{label}</span>
          {tag && <div className="text-[11px] text-slate-400 font-body truncate" data-id="element-5365" data-mp="14e7cmo45hrk0">{tag}</div>}
        </div>
        <div className="text-3xl font-bold text-[#0F172A] font-heading" data-id="element-5366" data-mp="14gk0tj0i4y5i">
          {value}
          {suffix && <span className="text-lg text-slate-400 font-semibold" data-id="element-5367" data-mp="11cmew6vrotwe">{suffix}</span>}
        </div>
        <div className={`text-sm font-body ${TONE_TEXT[tone]}`} data-id="element-5368" data-mp="11efjzhqqf62a">{note}</div>
        {detail && <div className="text-xs text-slate-400 font-body -mt-1" data-id="element-5369" data-mp="xd3wbhrf4uwq">{detail}</div>}
      </button>
      <div className="absolute top-4 right-4" data-id="element-5370" data-mp="1b8pf37726igp">
        <DateRangePicker value={dateRange} onChange={onDateRangeChange} data-id="element-5371" data-mp="16b5za2uppxmn" />
      </div>
    </div>;
}

/** Four KPIs on one row; each carries the shared period picker. */
export function DashboardKpis({
  stats: s,
  dateRange,
  onDateRangeChange,
  onSelect
}: DashboardKpisProps) {
  const {
    t,
    lang
  } = useLanguage();
  const vsPrev = t('vs période précédente', 'vs. previous period');
  const pts = s.onTime - s.onTimePrev;
  const dSat = Math.round((s.satisfaction - s.satisfactionPrev) * 10) / 10;
  const signed = (n: number, digits: string) => `${n > 0 ? '+' : n < 0 ? '−' : '±'}${digits}`;
  const picker = {
    dateRange,
    onDateRangeChange
  };
  return <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-5372" data-mp="1yy9zluiv1ns3">
      <KpiCard {...picker} label={t('Demandes reçues', 'Requests received')} value={fmtNum(s.received, lang)} note={`${pctChange(s.received, s.receivedPrev, lang)} ${vsPrev}`} tone="neutral" cta={t('Voir les demandes', 'View requests')} onClick={() => onSelect('requests')} data-id="element-5373" data-mp="2inhhof74b85r" />
      <KpiCard {...picker} label={t('Résolues dans les délais', 'Resolved on time')} value={`${s.onTime}`} suffix={lang === 'fr' ? ' %' : '%'} note={`${signed(pts, `${Math.abs(pts)}`)} ${t('points', 'points')} ${vsPrev}`} tone={pts >= 0 ? 'good' : 'bad'} cta={t('Voir le respect des SLA par service', 'View SLA compliance by service')} onClick={() => onSelect('sla')} data-id="element-5374" data-mp="2dowkkon5x4lj" />
      <KpiCard {...picker} tag={t(`À ce jour · ${TODAY_LABEL.fr}`, `As of today · ${TODAY_LABEL.en}`)} label={t('Demandes en retard', 'Late requests')} value={fmtNum(s.late, lang)} note={t(`${s.latePrev} il y a 30 jours`, `${s.latePrev} 30 days ago`)} tone={s.late > s.latePrev ? 'bad' : 'good'} detail={s.openAtRisk > 0 ? t(`+ ${s.openAtRisk} proches de l’échéance`, `+ ${s.openAtRisk} close to deadline`) : t('Aucune proche de l’échéance', 'None close to deadline')} cta={t('Voir les dossiers en retard', 'View late files')} onClick={() => onSelect('late')} data-id="element-5375" data-mp="1fz1nmix5gvw1" />
      <KpiCard {...picker} label={t('Satisfaction', 'Satisfaction')} value={fmtDec(s.satisfaction, lang)} suffix=" / 5" note={`${signed(dSat, fmtDec(Math.abs(dSat), lang))} ${vsPrev}`} tone={dSat >= 0 ? 'good' : 'bad'} detail={t(`${fmtNum(s.surveyResponses, 'fr')} réponses au sondage`, `${fmtNum(s.surveyResponses, 'en')} survey responses`)} cta={t('Voir les résultats', 'View results')} onClick={() => onSelect('survey')} data-id="element-5376" data-mp="20ifuc6tajbnn" />
    </div>;
}